import {  useState } from 'react'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import type { OrgItem } from '@/lib/apiTypes'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'
import { PlatformTabs, type Platform } from '@/features/integrations/components/PlatformTabs'
import { CurlSecretNote, CliCiNote } from '@/features/integrations/components/GuideNotes'
import {   CLI_INSTALL_GITHUB, CLI_GITLAB_SETUP, CLI_GITLAB_ID_TOKENS, TOKEN_EXCHANGE_CLI } from '@/features/integrations/snippets'

// ── Lock Files ─────────────────────────────────────────────────────────────────
export function LockFilesGuide({ org, onSwitchGuide }: { org: OrgItem | null; token: string | null; onSwitchGuide: (id: GuideId) => void }) {
  const [platform, setPlatform] = useState<Platform>('cli')
  const orgSlug = org?.slug ?? '<org-slug>'
  const wsName  = '<workspace>'

  const pushCurl = `curl -sf -X PUT \\
  "https://registry.terramantle.dev/state/${orgSlug}/${wsName}/terraform.lock.hcl" \\
  -u "terramantle-bot:$TF_HTTP_PASSWORD" \\
  --data-binary @.terraform.lock.hcl`

  const cliSnippet = `terramantle auth login

tofu init   # or: terraform init - generates .terraform.lock.hcl

# Upload ./.terraform.lock.hcl to the current context's workspace
terramantle lock push --workspace ${wsName}

# Or point it at another directory, and fail the command if the posture is at risk
terramantle lock push ./envs/prod --workspace ${wsName} --fail-on-atrisk

# Repo of workspaces? Fan out over every dir that has a lock file
terramantle state publish --all`

  const curlSnippet = `${TOKEN_EXCHANGE_CLI}
export TF_HTTP_PASSWORD=$TOKEN

terraform init   # generates .terraform.lock.hcl

${pushCurl}`

  const githubSnippet = `name: provider-lock-files

on:
  push:
    branches: [main]

permissions:
  contents: read
  id-token: write   # lets terramantle use the runner's ambient OIDC token

jobs:
  push-lock:
    runs-on: ubuntu-latest
    env:
      TERRAMANTLE_ORG: ${orgSlug}
    steps:
      - uses: actions/checkout@v4
      - uses: opentofu/setup-opentofu@v1

${CLI_INSTALL_GITHUB}

      - name: Push provider lock file
        run: |
          tofu init   # generates .terraform.lock.hcl
          terramantle lock push --workspace ${wsName} --fail-on-atrisk`

  const gitlabSnippet = `stages:
  - lock

push-lock:
  stage: lock
  image: ubuntu:24.04
${CLI_GITLAB_ID_TOKENS}
  variables:
    TERRAMANTLE_ORG: ${orgSlug}
${CLI_GITLAB_SETUP}
  script:
    - tofu init   # generates .terraform.lock.hcl
    - terramantle lock push --workspace ${wsName} --fail-on-atrisk
  only:
    - main`

  return (
    <div>
      <h3 style={s.guideTitle}>Provider Lock Files</h3>
      <p style={s.para}>
        Push your <code style={s.code}>.terraform.lock.hcl</code> after <code style={s.code}>terraform init</code> so
        Terramantle tracks which providers and versions are in use across your workspaces - and can enforce
        policy on them later.
      </p>

      <h3 style={s.subTitle}>Authenticate and push the lock file</h3>
      <p style={s.para}>
        This is a one-liner with the{' '}
        <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-getting-started')}>Terramantle CLI</button>:{' '}
        <code style={s.code}>terramantle lock push</code> finds the lock file, uploads it with the{' '}
        <code style={s.code}>origin</code> remote as provenance, and can gate the job on the resulting posture.
      </p>
      <PlatformTabs value={platform} onChange={setPlatform} />
      {platform === 'cli'    && <ShellSnippet code={cliSnippet}    language="bash" />}
      {platform === 'curl'   && <ShellSnippet code={curlSnippet}   language="bash" />}
      {platform === 'github' && <ShellSnippet code={githubSnippet} language="yaml" />}
      {platform === 'gitlab' && <ShellSnippet code={gitlabSnippet} language="yaml" />}

      {platform === 'cli' && (
        <p style={{ ...s.para, marginTop: 10 }}>
          Posture is eventually consistent, so <code style={s.code}>--fail-on-atrisk</code> waits for it (see{' '}
          <code style={s.code}>--posture-timeout</code> / <code style={s.code}>--require-posture</code>) and exits{' '}
          <code style={s.code}>3</code> when the gate trips -{' '}
          <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-commands')}>Commands</button> has the full
          exit-code table.
        </p>
      )}
      {platform === 'curl' && <CurlSecretNote />}
      {(platform === 'github' || platform === 'gitlab') && <CliCiNote onSwitchGuide={onSwitchGuide} />}

      <h3 style={s.subTitle}>Notes</h3>
      <ul style={s.list}>
        <li>Only files ending <code style={s.code}>terraform.lock.hcl</code> are accepted; the body is the raw lock file (10MB cap).</li>
        <li>Pushing requires workspace <strong>write</strong> permission - the same identity used for the state backend.</li>
        <li>This is separate from state locking - it simply records the providers and versions in use.</li>
      </ul>
    </div>
  )
}
